import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  alterarDissipacao, alterarVertical, campanhaEditavel, verDissipacao,
  type AlteracaoDaDissipacao, type CampanhaCompleta, type DissipacaoCompleta, type DissipacaoDaCampanha,
  type VerticalDaCampanha,
} from '../../api/cptu';
import {
  ajustarReta, interpretarDissipacao, valoresNaProfundidade,
  type Dissipacao, type InterpretacaoDaDissipacao, type Leituras, type Reta, type Resultado, type SaidaDaDissipacao,
} from '../../cptu/motor';
import {
  CurvaDeDissipacao, FRASE_T50_EXTRAPOLADO, leituras as contaDeLeituras, marcaDaSaidaDaDissipacao,
  numeroBr, type EixoDoTempo, type InsumoDaDissipacao, type MarcaDeHomologacao,
} from '../../cptu/visual';
import { cientifica, paraCampo, paraNumero, plural, sinal } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Aviso, CampoNumerico, Carregando, Dialogo, ErroDaApi, Vazio, useEstreito } from '../../ui';
import { AcaoBloqueadaPeloMotivo, AcoesBloqueadas, motivoDoBloqueio } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';
import { ESPERA_DO_RECALCULO, focarOPrimeiro, leiturasDaSessao, resultadoGravado, useFocoDeVolta } from './interpretacao';
import { NotasDoPorque } from './TabelaDaVertical';

/**
 * A dissipação aberta — onda 313, Tarefa 313.5 (spec 07 §5.5 e §6.1, item 5; a prancheta «3 · Dissipação», com as
 * correções R5–R7 e R11).
 *
 * À esquerda, as dissipações da campanha por vertical, cada uma com o t₅₀ («pela reta», e «extrapolado» quando a
 * curva não chega a u₅₀); no centro, a curva (√t | log t | t) com a reta do trecho inicial e, na vista √t, as duas
 * alças — arrastar cola na leitura mais próxima, o Shift solta; à direita, o resultado ao vivo: uᵢ; o u₀ — o da
 * vertical na profundidade (o hidrostático, ou o perfil por pontos) ao lado do informado, com a diferença escrita;
 * u₅₀; t₅₀; Ir = G₀/Su (da vertical ou informados); T*₅₀ · r (das premissas); cₕ e kₕ; o NA sugerido; observações.
 *
 * O resultado é do navegador (R2): a tela lê a curva (`GET /cptu/dissipacoes/:id`), as leituras da vertical e as
 * premissas dela, e calcula com o motor do `web`; o rascunho recalcula 150 ms depois da última mudança (R9) — as
 * alças não pulam nesse meio-tempo (`alcasVigentes`, na curva). A API guarda só as escolhas (`PATCH
 * /cptu/dissipacoes/:id`, só o que mudou): o u₀ (null = o da vertical), a reta (null = a automática), G₀, Su e M
 * (null = os da vertical, a leitura mais próxima) e as observações. A tela não troca o gravado sozinha: o u₀ que a
 * importação do `.cpt` grava (o do projeto do CPeT-IT, hidrostático com o NA na superfície) chega como informado, e
 * a escolha é do engenheiro (doc 296, pendência ao Humberto).
 *
 * Cada saída (t₅₀, Ir, cₕ, kₕ) leva a marca do R7: com todos os insumos dela informados, homologa-se a conta; com
 * algum da vertical, as premissas da vertical decidem (`marcaDaSaidaDaDissipacao`).
 *
 * Sem `cptu.editar`, ou com a campanha fora de rascunho/devolvida, tudo para ler e as ações à vista, bloqueadas com
 * o motivo (P-UI-24). No celular é consulta (R8): sem campo, sem alças, sem «Gravar». Sair com mudança não gravada
 * pede confirmação (P-TXT-09: «Sair sem gravar»). Um campo informado que não se lê (ou fora da faixa) bloqueia o
 * «Gravar», dizendo qual, como a Edição (rodada final da 313).
 *
 * Onda 315 (Tarefa 315.5b): ao lado da curva, a tabela tempo × u das leituras da dissipação, só para ler — a linha
 * escolhida acende o ponto na curva, e o clique na curva escolhe a linha (e a traz à vista); as leituras fora do
 * trecho da reta (o √t fora do trecho entre as duas pontas dela) vão marcadas «fora».
 */

/** As escolhas da dissipação que a API guarda (R11); null = o padrão — o u₀ e os G₀, Su e M da vertical, a reta automática. */
export interface EscolhasDaDissipacao {
  u0: number | null;
  reta: Reta | null;
  g0: number | null;
  su: number | null;
  m: number | null;
  observacoes: string | null;
}

const numeroOuNulo = (v: number | string | null | undefined): number | null =>
  (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** As escolhas gravadas de uma dissipação, como a API as devolve. */
export function escolhasGravadas(d: DissipacaoDaCampanha): EscolhasDaDissipacao {
  return {
    u0: numeroOuNulo(d.u0_kpa),
    reta: d.reta ? { x1: Number(d.reta.x1), y1: Number(d.reta.y1), x2: Number(d.reta.x2), y2: Number(d.reta.y2) } : null,
    g0: numeroOuNulo(d.g0_mpa),
    su: numeroOuNulo(d.su_kpa),
    m: numeroOuNulo(d.m_mpa),
    observacoes: d.observacoes ? d.observacoes : null,
  };
}

/** A dissipação na tela: a da API, a curva como o motor a lê, a vertical e o resultado gravado dela. */
export interface DissipacaoNaTela {
  d: DissipacaoCompleta;
  vertical: VerticalDaCampanha;
  r: Resultado;
  curva: Dissipacao;
}

/** A interpretação com um jogo de escolhas, os insumos da vertical na profundidade e o que foi informado à mão. */
export interface LeituraDaDissipacao {
  s: InterpretacaoDaDissipacao;
  daVertical: { g0MPa: number; suKPa: number; mMPa: number; u0KPa: number };
  informados: ReadonlySet<InsumoDaDissipacao>;
}

/**
 * A interpretação de uma dissipação com estas escolhas: o que não foi informado vem da própria vertical na
 * profundidade — a leitura mais próxima, sem janela (`valoresNaProfundidade`, R11) —, e γw, T*₅₀ e o raio, das
 * premissas da vertical.
 */
export function interpretar(x: DissipacaoNaTela, e: EscolhasDaDissipacao): LeituraDaDissipacao {
  const daVertical = valoresNaProfundidade(x.r, x.d.profundidade);
  const informados = new Set<InsumoDaDissipacao>();
  if (e.u0 !== null) informados.add('u0');
  if (e.g0 !== null) informados.add('g0');
  if (e.su !== null) informados.add('su');
  if (e.m !== null) informados.add('m');
  const s = interpretarDissipacao(x.curva, {
    profundidade: x.d.profundidade,
    u0: e.u0 ?? daVertical.u0KPa,
    reta: e.reta,
    g0MPa: e.g0 ?? daVertical.g0MPa,
    suKPa: e.su ?? daVertical.suKPa,
    mMPa: e.m ?? daVertical.mMPa,
    premissas: x.r.premissas,
  });
  return { s, daVertical, informados };
}

/** «t₅₀ 797 s · pela reta · extrapolado» — o resumo da lista (o t₅₀ é sempre pela reta, R11). */
export function resumoDoT50(s: InterpretacaoDaDissipacao): string {
  if (!Number.isFinite(s.t50)) return 't₅₀ sem valor: a reta não chega a u₅₀';
  return `t₅₀ ${numeroBr(s.t50, 0)} s · pela reta${s.t50Extrapolado ? ' · extrapolado' : ''}`;
}

/**
 * De onde veio a reta gravada: nenhuma é a automática; a que a tela gravou depois da importação (`reta_gravada`: um
 * UPDATE com a reta, não com o u₀, o G₀, o Su, o M ou as observações) é «gravada pela tela»; a do `.cpt`, a do
 * projeto do CPeT-IT (rodada da 313.5).
 */
export function origemDaReta(d: DissipacaoDaCampanha): string {
  if (!d.reta) return 'automática';
  if (d.reta_gravada) return 'gravada pela tela';
  return d.origem === 'cpeit_cpt' ? 'do projeto (CPeT-IT)' : 'do arquivo';
}

/**
 * As dissipações ativas da campanha, prontas para a tela: a curva de cada uma (`GET /cptu/dissipacoes/:id`), as
 * leituras das verticais delas (o cache da sessão) e o resultado gravado de cada vertical. Relê quando a campanha
 * relê; `trocar` põe no lugar a dissipação que o PATCH devolveu. Com `verticalId` (a edição da vertical, 313.6), só as
 * dela — as outras nem se buscam (R9).
 */
export function useDissipacoesNaTela(c: CampanhaCompleta, verticalId?: string): {
  itens: DissipacaoNaTela[] | null;
  erro: unknown;
  trocar(d: DissipacaoCompleta): void;
} {
  const [dados, setDados] = useState<{ ds: DissipacaoCompleta[]; leituras: Map<string, Leituras> } | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const ativas = useMemo(
    () => (verticalId ? c.dissipacoes.filter((d) => d.vertical_id === verticalId) : c.dissipacoes),
    [c.dissipacoes, verticalId],
  );
  useEffect(() => {
    let vale = true;
    void (async () => {
      try {
        const ds = await Promise.all(ativas.map((d) => verDissipacao(d.id)));
        const ids = [...new Set(ds.map((d) => d.vertical_id))];
        const ls = await Promise.all(ids.map((id) => leiturasDaSessao(id)));
        if (vale) {
          setDados({ ds, leituras: new Map(ids.map((id, k) => [id, ls[k]])) });
          setErro(null);
        }
      } catch (e) {
        if (vale) setErro(e);
      }
    })();
    return () => { vale = false; };
  }, [ativas]);
  const trocar = useCallback((d: DissipacaoCompleta) => {
    setDados((x) => (x ? { ...x, ds: x.ds.map((y) => (y.id === d.id ? d : y)) } : x));
  }, []);
  const itens = useMemo(() => {
    if (!dados) return null;
    const resultados = new Map<string, Resultado>();
    const saida: DissipacaoNaTela[] = [];
    for (const d of dados.ds) {
      const vertical = c.verticais.find((v) => v.id === d.vertical_id);
      const leituras = dados.leituras.get(d.vertical_id);
      if (!vertical || !leituras) continue;
      let r = resultados.get(vertical.id);
      if (!r) {
        r = resultadoGravado(c, vertical, leituras);
        resultados.set(vertical.id, r);
      }
      saida.push({
        d, vertical, r,
        curva: { tempo: Float64Array.from(d.tempo, Number), u: Float64Array.from(d.u, Number) },
      });
    }
    return saida;
  }, [dados, c]);
  return { itens, erro, trocar };
}

/** O resumo gravado de cada dissipação (a lista), calculado uma vez por releitura. */
export function useResumosGravados(itens: DissipacaoNaTela[] | null): Map<string, InterpretacaoDaDissipacao> {
  return useMemo(() => new Map((itens ?? []).map((i) => [i.d.id, interpretar(i, escolhasGravadas(i.d)).s])), [itens]);
}

const mesmoValor = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** O problema de um insumo informado (o texto do campo): não se lê, ou fora da faixa; undefined quando vale. */
function erroDoInsumo(texto: string, minimo: 'zero' | 'positivo'): string | undefined {
  const lido = paraNumero(texto);
  if (lido === undefined || !Number.isFinite(lido)) return 'Não se lê: use vírgula para as casas.';
  if (minimo === 'positivo' && lido <= 0) return 'Tem de ser maior que zero.';
  if (minimo === 'zero' && lido < 0) return 'É uma pressão: 0 ou mais.';
  return undefined;
}

/** Os insumos informados à mão: o rótulo do campo e a faixa (a mesma dos campos e da API). */
const INSUMOS: Readonly<Record<'u0' | 'g0' | 'su' | 'm', { rotulo: string; minimo: 'zero' | 'positivo' }>> = {
  u0: { rotulo: 'u₀ informado (kPa)', minimo: 'zero' },
  g0: { rotulo: 'G₀ informado (MPa)', minimo: 'positivo' },
  su: { rotulo: 'Su informado (kPa)', minimo: 'positivo' },
  m: { rotulo: 'M informado (MPa)', minimo: 'positivo' },
};
const duasCasas = (v: number): number => Math.round(v * 100) / 100;

const EIXOS: readonly [EixoDoTempo, string][] = [['raiz', '√t'], ['log', 'log t'], ['linear', 't']];
const SAIDAS: readonly SaidaDaDissipacao[] = ['t50', 'ir', 'ch', 'kh'];

/** A frase de quem só consulta no celular (R8), a mesma da tela da vertical. */
const SO_CONSULTA = 'Premissas, edição, camadas e dissipações se mudam no computador.';

export function DissipacaoInterpretada({ c, dissipacaoId, aoMudar }: {
  c: CampanhaCompleta;
  dissipacaoId: string;
  /** Uma escolha foi gravada (ou o NA da vertical): a casca relê a campanha. */
  aoMudar(): Promise<void>;
}) {
  const estreito = useEstreito();
  const { pode, eu } = useSessao();
  const navegar = useNavigate();
  const { itens, erro, trocar } = useDissipacoesNaTela(c);
  const resumos = useResumosGravados(itens);
  const x = itens?.find((i) => i.d.id === dissipacaoId) ?? null;
  const [eixo, setEixo] = useState<EixoDoTempo>('raiz');
  const [rascunho, setRascunho] = useState<Partial<EscolhasDaDissipacao>>({});
  const [doRascunho, setDoRascunho] = useState<LeituraDaDissipacao | null>(null);
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [janela, setJanela] = useState<'gravar' | 'na' | null>(null);
  /** A leitura escolhida na tabela ou na curva (o índice na curva); outra dissipação, nenhuma. */
  const [leitura, setLeitura] = useState<number | null>(null);
  useEffect(() => { setLeitura(null); }, [dissipacaoId]);
  const [saida, setSaida] = useState<string | null>(null);
  const idDaRetaAutomatica = useId();
  const idDoGravar = useId();
  const idDoBloqueio = useId();
  // As janelas devolvem o foco a quem as abriu; gravada, ele vai às ações do resultado (rodada do agent-browser da 313).
  useFocoDeVolta(janela !== null, '.cptu-dissipacao-acoes > button');
  useFocoDeVolta(saida !== null);

  const gravadas = useMemo(() => (x ? escolhasGravadas(x.d) : null), [x]);
  const gravado = useMemo(() => (x && gravadas ? interpretar(x, gravadas) : null), [x, gravadas]);
  const automatica = useMemo(() => (x ? ajustarReta(x.curva) : null), [x]);
  const sujo = Object.keys(rascunho).length > 0;

  // O rascunho recalcula 150 ms depois da última mudança (R9); sem rascunho, vale o gravado.
  useEffect(() => {
    if (!x || !gravadas || Object.keys(rascunho).length === 0) {
      setDoRascunho(null);
      return undefined;
    }
    const t = setTimeout(() => setDoRascunho(interpretar(x, { ...gravadas, ...rascunho })), ESPERA_DO_RECALCULO);
    return () => clearTimeout(t);
  }, [x, gravadas, rascunho]);

  /*
   * Sair com mudança não gravada pede confirmação: o clique num link interno da página (a lista, as abas, o caminho)
   * é segurado antes do roteador e a janela pergunta; o F5 e o fechar da aba ficam com o aviso do navegador (a única
   * porta que ele dá a uma página).
   */
  useEffect(() => {
    if (!sujo) return undefined;
    const aoClicar = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement) || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setSaida(`${url.pathname}${url.search}${url.hash}`);
    };
    const aoSairDaPagina = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('click', aoClicar, true);
    window.addEventListener('beforeunload', aoSairDaPagina);
    return () => {
      window.removeEventListener('click', aoClicar, true);
      window.removeEventListener('beforeunload', aoSairDaPagina);
    };
  }, [sujo]);

  if (erro && !itens) return <ErroDaApi erro={erro} />;
  if (!itens) return <Carregando texto="Abrindo a dissipação e calculando a interpretação" />;
  if (!x || !gravadas || !gravado || !automatica) {
    return (
      <section className="cartao">
        <Vazio>
          Esta dissipação não está entre as ativas da campanha (uma reimportação a substituiu?).{' '}
          <Link to={`/cptu/${c.campanha.id}/dissipacoes`}>Ver as dissipações da campanha</Link>
        </Vazio>
      </section>
    );
  }

  const valendo: EscolhasDaDissipacao = { ...gravadas, ...rascunho };
  const atual = sujo ? (doRascunho ?? gravado) : gravado;
  const s = atual.s;
  const p = x.r.premissas;
  const z = x.d.profundidade;
  const titulo = `${x.d.vertical} · ${numeroBr(z, 2)} m`;
  const bloqueio = !pode('cptu.editar') ? 'permissao' : !campanhaEditavel(c.campanha.situacao) ? 'parada' : null;
  const podeMudar = !estreito && !bloqueio;
  // As ações à vista, bloqueadas (P-UI-24); o motivo vai uma vez só, com o «Gravar», e as outras apontam para ele.
  const motivoBloqueado = bloqueio
    ? motivoDoBloqueio(bloqueio, 'não grava a interpretação do CPTu', c.campanha.situacao, eu?.atuandoPor ? eu.nome : null)
    : '';
  const bloqueada = (texto: string) => (
    <AcaoBloqueadaPeloMotivo key={texto} texto={texto} motivo={motivoBloqueado} idDoMotivo={idDoBloqueio} />
  );

  /** Uma escolha mudou: entra no rascunho se difere do gravado; igual a ele, sai. */
  const mudar = (m: Partial<EscolhasDaDissipacao>) => {
    setRascunho((r) => {
      const prox: Partial<EscolhasDaDissipacao> = { ...r };
      for (const k of Object.keys(m) as (keyof EscolhasDaDissipacao)[]) {
        if (mesmoValor(m[k], gravadas[k])) delete prox[k];
        else (prox as Record<string, unknown>)[k] = m[k];
      }
      return prox;
    });
  };
  const sairSemGravar = () => {
    setRascunho({});
    setTextos({});
  };

  // As marcas do R7, com o porquê numerado embaixo (P-UI-44).
  const marcas = Object.fromEntries(SAIDAS.map((k) => [k, marcaDaSaidaDaDissipacao(k, {
    premissas: p, informados: atual.informados,
  })])) as Record<SaidaDaDissipacao, MarcaDeHomologacao>;
  const motivos: string[] = [];
  for (const k of SAIDAS) {
    const m = marcas[k];
    if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);
  }
  const marca = (k: SaidaDaDissipacao): ReactNode => {
    const m = marcas[k];
    return !m.homologada && m.motivo ? <span className="cptu-marca-texto"> ▲ ({motivos.indexOf(m.motivo) + 1})</span> : null;
  };

  /** O campo numérico de uma escolha: o texto como foi digitado; o número que se lê vai ao rascunho. */
  const campo = (chave: 'u0' | 'g0' | 'su' | 'm', rotulo: string, valor: number, minimo: 'zero' | 'positivo') => {
    const texto = textos[chave] ?? paraCampo(valor, 4);
    const erroDoCampo = erroDoInsumo(texto, minimo);
    return (
      <CampoNumerico rotulo={rotulo} value={texto} erro={erroDoCampo}
        onChange={(e) => {
          const t = e.target.value;
          setTextos((y) => ({ ...y, [chave]: t }));
          const n = paraNumero(t);
          if (n !== undefined && (minimo === 'zero' ? n >= 0 : n > 0)) mudar({ [chave]: n } as Partial<EscolhasDaDissipacao>);
        }} />
    );
  };

  // ── o u₀ ──
  const u0Vertical = atual.daVertical.u0KPa;
  const naTexto = x.vertical.nivel_agua === null || x.vertical.nivel_agua === undefined
    ? 'NA não informado: na superfície' : `NA ${numeroBr(p.na, 2)} m`;
  const modoDoU0 = p.u0Modo === 'pontos' ? 'perfil por pontos' : 'hidrostático';
  const u0DaVertical = Number.isFinite(u0Vertical)
    ? `da vertical na profundidade: ${numeroBr(u0Vertical, 2)} kPa (${naTexto}, ${modoDoU0})`
    : 'da vertical na profundidade: sem valor (fora das leituras da vertical)';
  const u0Informado = valendo.u0;
  const diferencaDoU0 = u0Informado !== null && Number.isFinite(u0Vertical) ? u0Informado - u0Vertical : NaN;
  const u0DoProjeto = u0Informado !== null && u0Informado === gravadas.u0 && x.d.origem === 'cpeit_cpt'
    && Math.abs(u0Informado - p.gamaW * z) < 0.005;

  // ── G₀, Su e M ──
  const g0su = valendo.g0 !== null || valendo.su !== null;
  const textoG0Su = (g0: number, su: number) => `${numeroBr(g0, 2)} MPa / ${numeroBr(su, 2)} kPa`;

  // ── a reta ──
  const textoDaReta = valendo.reta === null
    ? (automatica.motivo
      ? `Reta automática: ${automatica.motivo}`
      : `Reta automática do motor (o trecho reto do máximo de u até o joelho da curva): r² ${numeroBr(automatica.r2, 4)}.`)
    : rascunho.reta !== undefined
      ? 'Reta ajustada pelas alças — ainda não gravada.'
      : `Reta ${origemDaReta(x.d)}.`;
  const tempos = Array.from(x.curva.tempo).filter(Number.isFinite);
  const faixaDoTempo = tempos.length > 0
    ? `${numeroBr(Math.min(...tempos), 0)} a ${numeroBr(Math.max(...tempos), 0)} s` : 'sem tempo';

  const textoDoT50 = Number.isFinite(s.t50) ? `${numeroBr(s.t50, 2)} s (${numeroBr(s.t50 / 60, 1)} min)` : '—';

  /*
   * O campo informado que não se lê (ou fora da faixa) fica com o último número que se leu no rascunho: gravar assim
   * gravaria outra coisa que a tela mostra. Ele bloqueia o «Gravar», dizendo qual (como a Edição). Só os campos à
   * vista: o insumo «da vertical» não tem campo.
   */
  const aVista: Record<keyof typeof INSUMOS, boolean> = { u0: u0Informado !== null, g0: g0su, su: g0su, m: valendo.m !== null };
  const problemas = (Object.keys(INSUMOS) as (keyof typeof INSUMOS)[])
    .filter((k) => aVista[k] && textos[k] !== undefined)
    .map((k) => ({ k, erro: erroDoInsumo(textos[k], INSUMOS[k].minimo) }))
    .filter((x): x is { k: keyof typeof INSUMOS; erro: string } => !!x.erro)
    .map((x) => `«${INSUMOS[x.k].rotulo}» ${x.erro.charAt(0).toLowerCase()}${x.erro.slice(1).replace(/\.$/, '')}`);
  const motivoDoGravar = !sujo ? 'Nada mudou ainda.'
    : problemas.length > 0
      ? `Confira antes de gravar: ${problemas[0]}${problemas.length > 1 ? ` (e mais ${problemas.length - 1})` : ''}.` : null;

  return (
    <section className="cptu-dissipacao-tela" aria-label={`Dissipação ${titulo}`}>
      {estreito ? (
        <label className="campo cptu-escolha-dissipacao">
          <span className="rotulo">Dissipação</span>
          <select value={x.d.id} onChange={(e) => navegar(`/cptu/${c.campanha.id}/dissipacoes/${e.target.value}`)}>
            {itens.map((i) => (
              <option key={i.d.id} value={i.d.id}>{i.d.vertical} · {numeroBr(i.d.profundidade, 2)} m</option>
            ))}
          </select>
        </label>
      ) : (
        <ListaDasDissipacoes c={c} itens={itens} aberta={x.d.id} resumos={resumos} daAberta={s} />
      )}

      <div className="cptu-dissipacao-centro">
        <header className="cptu-cabeca-dissipacao">
          <h2 className="mono">{titulo}</h2>
          <p>
            {contaDeLeituras(x.d.pares)} · {faixaDoTempo}
            {x.d.arquivo ? <> · arquivo <span className="mono">{x.d.arquivo}</span></> : null}
          </p>
        </header>
        {erro ? <ErroDaApi erro={erro} /> : null}
        {estreito && <Aviso tipo="info">{SO_CONSULTA}</Aviso>}
        <div className="cptu-barra-dissipacao">
          <div className="cptu-segmento" role="group" aria-label="Eixo do tempo">
            {EIXOS.map(([e, texto]) => (
              <button key={e} type="button" className="cptu-segmento-opcao" aria-pressed={eixo === e} onClick={() => setEixo(e)}>
                {texto}
              </button>
            ))}
          </div>
          {!estreito && (bloqueio ? bloqueada('Reta automática') : (
            <>
              <button type="button" className="botao secundario" aria-disabled={valendo.reta === null || undefined}
                aria-describedby={valendo.reta === null ? idDaRetaAutomatica : undefined}
                onClick={() => { if (valendo.reta !== null) mudar({ reta: null }); }}>
                Reta automática
              </button>
              {valendo.reta === null && (
                <small id={idDaRetaAutomatica} className="cptu-motivo-bloqueio">A reta já é a automática.</small>
              )}
            </>
          ))}
        </div>
        <div className="cptu-curva-e-tabela">
          <div className="cptu-curva-e-nota">
            <CurvaDeDissipacao tempo={x.curva.tempo} u={x.curva.u} eixo={eixo} interpretacao={s} editavel={podeMudar}
              aoMudarReta={podeMudar ? (r) => mudar({ reta: r }) : undefined}
              leituraEscolhida={leitura} aoEscolherLeitura={setLeitura} />
          </div>
          <TabelaDaCurva tempo={x.curva.tempo} u={x.curva.u} reta={s.reta} escolhida={leitura} aoEscolher={setLeitura} />
        </div>
        <p className="cptu-nota">{textoDaReta} uᵢ é a reta em t = 0; o u₀ é a linha em azul-água, o u₅₀ a tracejada.</p>
      </div>

      <aside className="cptu-dissipacao-resultado" aria-label="Resultado da dissipação">
        <h3>Resultado</h3>
        <Saida chave="ui" rotulo="uᵢ inicial (reta em t = 0)" valor={`${numeroBr(s.ui, 2)} kPa`} />

        <Saida chave="u0" rotulo="u₀" valor={`${numeroBr(s.u0, 2)} kPa`}>
          {podeMudar ? (
            <fieldset className="cptu-escolha-insumo">
              <legend className="so-leitor">De onde vem o u₀</legend>
              <label className="cptu-radio">
                <input type="radio" name={`u0-${x.d.id}`} value="vertical" checked={u0Informado === null}
                  onChange={() => mudar({ u0: null })} />
                <span>{u0DaVertical}</span>
              </label>
              <label className="cptu-radio">
                <input type="radio" name={`u0-${x.d.id}`} value="informado" checked={u0Informado !== null}
                  onChange={() => mudar({
                    u0: gravadas.u0 ?? duasCasas(Number.isFinite(u0Vertical) ? u0Vertical : p.gamaW * z),
                  })} />
                <span>informado</span>
              </label>
              {u0Informado !== null && campo('u0', INSUMOS.u0.rotulo, u0Informado, 'zero')}
            </fieldset>
          ) : (
            <>
              {u0Informado !== null && <p className="cptu-nota">informado: {numeroBr(u0Informado, 2)} kPa</p>}
              <p className="cptu-nota">{u0DaVertical}</p>
            </>
          )}
          {Number.isFinite(diferencaDoU0) && (
            <p className="cptu-nota">diferença do informado: {sinal(diferencaDoU0, 2)} kPa em relação ao da vertical</p>
          )}
          {u0DoProjeto && <p className="cptu-nota">o do projeto do CPeT-IT: hidrostático com o NA na superfície</p>}
        </Saida>

        <Saida chave="u50" rotulo="u₅₀ = (uᵢ + u₀)/2" valor={`${numeroBr(s.u50, 2)} kPa`} />

        <Saida chave="t50" rotulo="t₅₀" valor={textoDoT50} marca={marca('t50')}>
          <p className={s.t50Extrapolado ? 'cptu-aviso' : 'cptu-nota'}>
            pela reta{s.t50Extrapolado ? ` · ${FRASE_T50_EXTRAPOLADO}` : ''}
          </p>
        </Saida>

        <Saida chave="ir" rotulo="Ir = G₀/Su" valor={numeroBr(s.ir, 2)} marca={marca('ir')}>
          {podeMudar ? (
            <fieldset className="cptu-escolha-insumo">
              <legend className="so-leitor">De onde vêm o G₀ e o Su</legend>
              <label className="cptu-radio">
                <input type="radio" name={`g0su-${x.d.id}`} value="vertical" checked={!g0su}
                  onChange={() => mudar({ g0: null, su: null })} />
                <span>da vertical: {textoG0Su(atual.daVertical.g0MPa, atual.daVertical.suKPa)}</span>
              </label>
              <label className="cptu-radio">
                <input type="radio" name={`g0su-${x.d.id}`} value="informados" checked={g0su}
                  onChange={() => mudar({
                    g0: gravadas.g0 ?? (Number.isFinite(atual.daVertical.g0MPa) ? duasCasas(atual.daVertical.g0MPa) : 1),
                    su: gravadas.su ?? (Number.isFinite(atual.daVertical.suKPa) ? duasCasas(atual.daVertical.suKPa) : 1),
                  })} />
                <span>informados</span>
              </label>
              {g0su && (
                <div className="cptu-insumos-informados">
                  {campo('g0', INSUMOS.g0.rotulo, valendo.g0 ?? atual.daVertical.g0MPa, 'positivo')}
                  {campo('su', INSUMOS.su.rotulo, valendo.su ?? atual.daVertical.suKPa, 'positivo')}
                </div>
              )}
            </fieldset>
          ) : (
            <p className="cptu-nota">
              {g0su
                ? `informados: ${textoG0Su(valendo.g0 ?? atual.daVertical.g0MPa, valendo.su ?? atual.daVertical.suKPa)}`
                : `da vertical: ${textoG0Su(atual.daVertical.g0MPa, atual.daVertical.suKPa)}`}
            </p>
          )}
        </Saida>

        <Saida chave="t50Fator" rotulo="T*₅₀ (Teh & Houlsby, u₂) · r"
          valor={`${numeroBr(p.t50Fator, 3)} · ${numeroBr(p.raioCone * 100, 2)} cm`}>
          <p className="cptu-nota">das premissas da vertical</p>
        </Saida>

        {/* A notação científica da casa (`cientifica`, P-FMT-01): três algarismos, «2,72 × 10⁻⁶», como o laudo. */}
        <Saida chave="ch" rotulo="cₕ = T*₅₀·r²·√Ir/t₅₀"
          valor={`${cientifica(s.ch, 2)} m²/s · ${numeroBr(s.chAno, 2)} m²/ano`} marca={marca('ch')} />

        <Saida chave="kh" rotulo="kₕ = cₕ·γw/M" valor={`${cientifica(s.kh, 2)} m/s`} marca={marca('kh')}>
          {podeMudar ? (
            <fieldset className="cptu-escolha-insumo">
              <legend className="so-leitor">De onde vem o M</legend>
              <label className="cptu-radio">
                <input type="radio" name={`m-${x.d.id}`} value="vertical" checked={valendo.m === null}
                  onChange={() => mudar({ m: null })} />
                <span>M da vertical: {numeroBr(atual.daVertical.mMPa, 2)} MPa</span>
              </label>
              <label className="cptu-radio">
                <input type="radio" name={`m-${x.d.id}`} value="informado" checked={valendo.m !== null}
                  onChange={() => mudar({
                    m: gravadas.m ?? (Number.isFinite(atual.daVertical.mMPa) ? duasCasas(atual.daVertical.mMPa) : 1),
                  })} />
                <span>M informado</span>
              </label>
              {valendo.m !== null && campo('m', INSUMOS.m.rotulo, valendo.m, 'positivo')}
            </fieldset>
          ) : (
            <p className="cptu-nota">
              {valendo.m !== null
                ? `M informado: ${numeroBr(valendo.m, 2)} MPa`
                : `M da vertical: ${numeroBr(atual.daVertical.mMPa, 2)} MPa`}
            </p>
          )}
        </Saida>

        <Saida chave="na" rotulo="NA pela dissipação"
          valor={s.naEstimado === null ? 'sem sugestão' : `NA sugerido ${numeroBr(s.naEstimado, 2)} m`}>
          {s.naEstimado === null ? (
            <p className="cptu-nota">sem sugestão — a curva não parou</p>
          ) : !estreito && (
            bloqueio ? bloqueada('Usar como NA da vertical') : (
              <button type="button" className="botao secundario" onClick={() => setJanela('na')}>
                Usar como NA da vertical
              </button>
            )
          )}
        </Saida>

        <div className="cptu-saida" data-saida="observacoes">
          {podeMudar ? (
            <Area rotulo="Observações" rows={2} maxLength={4000} value={valendo.observacoes ?? ''}
              onChange={(e) => mudar({ observacoes: e.target.value.trim() ? e.target.value : null })} />
          ) : (
            <>
              <span className="cptu-saida-rotulo">Observações</span>
              <p className="cptu-nota">{valendo.observacoes ?? 'nenhuma'}</p>
            </>
          )}
        </div>

        <NotasDoPorque motivos={motivos} />

        {!estreito && (
          <div className="cptu-dissipacao-acoes">
            {bloqueio ? <AcoesBloqueadas textos={['Gravar']} motivo={motivoBloqueado} id={idDoBloqueio} /> : (
              <>
                <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
                  aria-describedby={motivoDoGravar ? idDoGravar : undefined}
                  onClick={() => { if (!motivoDoGravar) setJanela('gravar'); }}>
                  Gravar
                </button>
                {sujo && (
                  <button type="button" className="botao secundario" onClick={() => {
                    sairSemGravar();
                    // O botão some: o foco vai ao «Gravar» que fica (rodada do agent-browser da 313).
                    focarOPrimeiro('.cptu-dissipacao-acoes > button');
                  }}>Sair sem gravar</button>
                )}
                {motivoDoGravar && <small id={idDoGravar} className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
              </>
            )}
          </div>
        )}
      </aside>

      {janela === 'gravar' && (
        <JanelaDeGravar x={x} gravadas={gravadas} rascunho={rascunho} daVertical={atual.daVertical}
          aoFechar={() => setJanela(null)}
          aoPronto={async (d) => {
            trocar(d);
            setJanela(null);
            sairSemGravar();
            await aoMudar();
          }} />
      )}
      {janela === 'na' && s.naEstimado !== null && (
        <JanelaDoNa x={x} na={s.naEstimado} aoFechar={() => setJanela(null)}
          aoPronto={async () => { setJanela(null); await aoMudar(); }} />
      )}
      {saida && (
        <Dialogo aberto titulo="Sair sem gravar?" rotulo={`Dissipação ${titulo}`} aoFechar={() => setSaida(null)}>
          <p className="sub">
            As escolhas desta dissipação que ainda não foram gravadas se perdem: {mudancasEscritas(rascunho).join(', ')}.
          </p>
          <div className="rodape-dialogo">
            <button type="button" className="botao perigo" onClick={() => {
              const para = saida;
              setSaida(null);
              sairSemGravar();
              navegar(para);
            }}>
              Sair sem gravar
            </button>
            <button type="button" className="botao secundario" onClick={() => setSaida(null)}>Cancelar</button>
          </div>
        </Dialogo>
      )}
    </section>
  );
}

/**
 * A tabela tempo × u da dissipação aberta (315.5b), só para ler: uma linha por leitura, na ordem do arquivo, com o t,
 * o √t, o u e se a leitura cai no trecho da reta (o √t entre as duas pontas dela) ou fora. A linha escolhida leva
 * `aria-current` e acende o ponto na curva; escolhida pela curva, a tabela a traz à vista.
 */
function TabelaDaCurva({ tempo, u, reta, escolhida, aoEscolher }: {
  tempo: ArrayLike<number>; u: ArrayLike<number>; reta: Reta; escolhida: number | null; aoEscolher(i: number): void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const lo = Math.min(reta.x1, reta.x2);
  const hi = Math.max(reta.x1, reta.x2);
  const linhas = useMemo(() => Array.from({ length: Math.min(tempo.length, u.length) }, (_, i) => {
    const raiz = Number.isFinite(tempo[i]) && tempo[i] >= 0 ? Math.sqrt(tempo[i]) : NaN;
    return { i, t: tempo[i], raiz, u: u[i], fora: !(raiz >= lo - 1e-9 && raiz <= hi + 1e-9) };
  }), [tempo, u, lo, hi]);
  const fora = linhas.filter((l) => l.fora).length;
  // Escolhida pela curva, a linha vem à vista dentro da caixa (a página não pula).
  useEffect(() => {
    const el = caixa.current;
    if (!el || escolhida === null) return;
    const tr = el.querySelector<HTMLTableRowElement>(`tr[data-leitura="${escolhida}"]`);
    if (!tr) return;
    const a = tr.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    if (a.top < b.top + a.height || a.bottom > b.bottom) el.scrollTop += a.top - b.top - (b.height - a.height) / 2;
  }, [escolhida]);
  return (
    <section className="cartao cptu-tabela-da-curva" aria-label="As leituras da dissipação">
      <p className="cptu-tabela-conta">
        {contaDeLeituras(linhas.length)} · {fora === 0 ? 'todas no trecho da reta' : `${plural(fora, 'fora do trecho da reta', 'fora do trecho da reta')}`}
        {' · '}clique numa linha (ou na curva) para acender o ponto
      </p>
      <div className="rola congelada cptu-tabela-da-curva-caixa" ref={caixa} tabIndex={0} role="region"
        aria-label="As leituras da dissipação (rola por dentro)">
        <table aria-label="Tempo × u da dissipação">
          <thead>
            <tr>
              <th scope="col" className="n">t (s)</th>
              <th scope="col" className="n">√t (√s)</th>
              <th scope="col" className="n">u (kPa)</th>
              <th scope="col">Trecho da reta</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.i} data-leitura={l.i} aria-current={l.i === escolhida ? 'true' : undefined}
                className={[l.fora ? 'fora' : '', l.i === escolhida ? 'escolhida' : ''].filter(Boolean).join(' ') || undefined}
                onClick={() => aoEscolher(l.i)}>
                <td className="n mono">
                  <button type="button" className="cptu-z-da-linha" aria-label={`t ${numeroBr(l.t, 1)} s: acender o ponto na curva`}
                    onClick={(e) => { e.stopPropagation(); aoEscolher(l.i); }}>
                    {numeroBr(l.t, 1)}
                  </button>
                </td>
                <td className="n mono">{numeroBr(l.raiz, 2)}</td>
                <td className="n mono">{numeroBr(l.u, 2)}</td>
                <td>{l.fora ? 'fora' : 'na reta'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Uma linha do resultado: o rótulo, o valor (com a marca do R7) e o que vem embaixo. */
function Saida({ chave, rotulo, valor, marca, children }: {
  chave: string; rotulo: string; valor: string; marca?: ReactNode; children?: ReactNode;
}) {
  return (
    <div className="cptu-saida" data-saida={chave}>
      <div className="cptu-saida-linha">
        <span className="cptu-saida-rotulo">{rotulo}</span>
        <span><span className="cptu-valor">{valor}</span>{marca}</span>
      </div>
      {children}
    </div>
  );
}

/** As dissipações da campanha por vertical, a aberta marcada, cada uma com o t₅₀ (a aberta, ao vivo). */
function ListaDasDissipacoes({ c, itens, aberta, resumos, daAberta }: {
  c: CampanhaCompleta;
  itens: DissipacaoNaTela[];
  aberta: string;
  resumos: Map<string, InterpretacaoDaDissipacao>;
  daAberta: InterpretacaoDaDissipacao;
}) {
  const verticais = c.verticais.filter((v) => v.situacao === 'ativa');
  const grupos = verticais.map((v) => ({ v, ds: itens.filter((i) => i.vertical.id === v.id) }));
  const semDissipacao = grupos.some((g) => g.ds.length === 0);
  return (
    <nav className="cptu-lista-dissipacoes" aria-label="Dissipações da campanha">
      {grupos.filter((g) => g.ds.length > 0).map(({ v, ds }) => (
        <div key={v.id} className="cptu-grupo-dissipacoes">
          <h3 className="mono">{v.codigo}</h3>
          {ds.map((i) => {
            const s = i.d.id === aberta ? daAberta : resumos.get(i.d.id);
            return (
              <Link key={i.d.id} className="cptu-item-dissipacao" to={`/cptu/${c.campanha.id}/dissipacoes/${i.d.id}`}
                aria-current={i.d.id === aberta ? 'page' : undefined}>
                <b className="mono">{numeroBr(i.d.profundidade, 2)} m</b>
                <span className="cptu-item-dissipacao-t50">{s ? resumoDoT50(s) : 't₅₀ calculando…'}</span>
              </Link>
            );
          })}
        </div>
      ))}
      {semDissipacao && <p className="cptu-nota-da-lista">As outras verticais não têm dissipação.</p>}
    </nav>
  );
}

/** O que está no rascunho, por extenso (a janela de sair e a de gravar). */
function mudancasEscritas(r: Partial<EscolhasDaDissipacao>): string[] {
  const nomes: Record<keyof EscolhasDaDissipacao, string> = {
    u0: 'o u₀', reta: 'a reta', g0: 'o G₀', su: 'o Su', m: 'o M', observacoes: 'as observações',
  };
  return (Object.keys(r) as (keyof EscolhasDaDissipacao)[]).map((k) => nomes[k]);
}

/**
 * Gravar as escolhas com motivo — só o que mudou vai ao `PATCH /cptu/dissipacoes/:id` (R11); null volta ao padrão
 * (o u₀ e os G₀, Su e M da vertical, a reta automática). A janela lista o antes e o depois de cada uma.
 */
function JanelaDeGravar({ x, gravadas, rascunho, daVertical, aoFechar, aoPronto }: {
  x: DissipacaoNaTela;
  gravadas: EscolhasDaDissipacao;
  rascunho: Partial<EscolhasDaDissipacao>;
  daVertical: LeituraDaDissipacao['daVertical'];
  aoFechar(): void;
  aoPronto(d: DissipacaoCompleta): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;

  const textoDoU0 = (v: number | null) => (v === null
    ? `da vertical (${numeroBr(daVertical.u0KPa, 2)} kPa)` : `informado ${numeroBr(v, 2)} kPa`);
  const textoDaReta = (v: Reta | null, gravada: boolean) => {
    if (v === null) return 'automática';
    if (gravada) return origemDaReta(x.d);
    const inclinacao = (v.y2 - v.y1) / (v.x2 - v.x1);
    return `ajustada pelas alças (uᵢ ${numeroBr(v.y1 - inclinacao * v.x1, 2)} kPa)`;
  };
  const textoDoInsumo = (v: number | null, daV: number, unidade: string) => (v === null
    ? `da vertical (${numeroBr(daV, 2)} ${unidade})` : `${numeroBr(v, 2)} ${unidade}`);
  const linhas: string[] = [];
  if (rascunho.u0 !== undefined) linhas.push(`u₀: ${textoDoU0(gravadas.u0)} → ${textoDoU0(rascunho.u0)}`);
  if (rascunho.reta !== undefined) linhas.push(`reta: ${textoDaReta(gravadas.reta, true)} → ${textoDaReta(rascunho.reta, false)}`);
  if (rascunho.g0 !== undefined) linhas.push(`G₀: ${textoDoInsumo(gravadas.g0, daVertical.g0MPa, 'MPa')} → ${textoDoInsumo(rascunho.g0, daVertical.g0MPa, 'MPa')}`);
  if (rascunho.su !== undefined) linhas.push(`Su: ${textoDoInsumo(gravadas.su, daVertical.suKPa, 'kPa')} → ${textoDoInsumo(rascunho.su, daVertical.suKPa, 'kPa')}`);
  if (rascunho.m !== undefined) linhas.push(`M: ${textoDoInsumo(gravadas.m, daVertical.mMPa, 'MPa')} → ${textoDoInsumo(rascunho.m, daVertical.mMPa, 'MPa')}`);
  if (rascunho.observacoes !== undefined) linhas.push('as observações');

  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    const corpo: AlteracaoDaDissipacao = { motivo: motivo.trim() };
    if (rascunho.u0 !== undefined) corpo.u0Kpa = rascunho.u0;
    if (rascunho.reta !== undefined) corpo.reta = rascunho.reta;
    if (rascunho.g0 !== undefined) corpo.g0Mpa = rascunho.g0;
    if (rascunho.su !== undefined) corpo.suKpa = rascunho.su;
    if (rascunho.m !== undefined) corpo.mMpa = rascunho.m;
    if (rascunho.observacoes !== undefined) corpo.observacoes = rascunho.observacoes;
    try {
      await aoPronto(await alterarDissipacao(x.d.id, corpo));
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto titulo="Gravar as escolhas da dissipação" rotulo={`${x.d.vertical} · ${numeroBr(x.d.profundidade, 2)} m`}
      aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          Vai para a dissipação: só as escolhas que mudaram. A curva medida não muda; o t₅₀, o Ir, o cₕ e o kₕ são da
          tela, recalculados com elas.
        </p>
        <ul className="cptu-lista-da-janela">{linhas.map((l) => <li key={l}>{l}</li>)}</ul>
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/** O NA sugerido pela dissipação vira o NA da vertical (o cabeçalho, `nivelAgua`), com motivo — sugestão, nunca sozinho. */
function JanelaDoNa({ x, na, aoFechar, aoPronto }: {
  x: DissipacaoNaTela; na: number; aoFechar(): void; aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const novo = duasCasas(na);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;
  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      await alterarVertical(x.vertical.id, { nivelAgua: novo, motivo: motivo.trim() });
      await aoPronto();
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };
  const antes = x.vertical.nivel_agua === null || x.vertical.nivel_agua === undefined
    ? 'não informado' : `${numeroBr(x.vertical.nivel_agua, 2)} m`;
  return (
    <Dialogo aberto titulo="Usar como NA da vertical" rotulo={`Vertical ${x.vertical.codigo}`} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          O NA da vertical {x.vertical.codigo} passa de {antes} a {numeroBr(novo, 2)} m (o sugerido pela dissipação de{' '}
          {numeroBr(x.d.profundidade, 2)} m, com a curva parada). O u₀ hidrostático, o σ&apos;v e o que depende deles
          recalculam em toda a vertical.
        </p>
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}
